import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Channel configuration based on your menu structure
const allChannels = [
  // Community Hub
  {
    id: "guidelines",
    name: "Club Guidelines",
    icon: "📋",
    path: "/community/club-guidelines",
    category: "hub",
  },
  {
    id: "introductions",
    name: "Introductions",
    icon: "👋",
    path: "/community/introductions",
    category: "hub",
  },

  // India Jobs
  {
    id: "in-freshers",
    name: "Freshers",
    icon: "🎓",
    path: "/community/in/freshers",
    region: "india",
  },
  {
    id: "in-operations",
    name: "Operations",
    icon: "⚙️",
    path: "/community/in/operations",
    region: "india",
  },
  {
    id: "in-program",
    name: "Program Mgmt",
    icon: "📊",
    path: "/community/in/program",
    region: "india",
  },
  {
    id: "in-product",
    name: "Product Mgmt",
    icon: "🚀",
    path: "/community/in/product",
    region: "india",
  },
  {
    id: "in-marketing",
    name: "Marketing",
    icon: "📱",
    path: "/community/in/marketing",
    region: "india",
  },
  {
    id: "in-account",
    name: "Sales",
    icon: "💼",
    path: "/community/in/account",
    region: "india",
  },
  {
    id: "in-category",
    name: "Category Mgmt",
    icon: "🏷️",
    path: "/community/in/category",
    region: "india",
  },
  {
    id: "in-finance",
    name: "Finance",
    icon: "💰",
    path: "/community/in/finance",
    region: "india",
  },
  {
    id: "in-hr",
    name: "HR",
    icon: "👥",
    path: "/community/in/hr",
    region: "india",
  },
  {
    id: "in-analyst",
    name: "Analytics",
    icon: "📈",
    path: "/community/in/analyst",
    region: "india",
  },
  {
    id: "in-strategy",
    name: "Strategy",
    icon: "🎯",
    path: "/community/in/strategy",
    region: "india",
  },

  // US Jobs
  {
    id: "us-operations",
    name: "Operations",
    icon: "⚙️",
    path: "/community/us/operations",
    region: "us",
  },
  {
    id: "us-program",
    name: "Program Mgmt",
    icon: "📊",
    path: "/community/us/program",
    region: "us",
  },
  {
    id: "us-product",
    name: "Product Mgmt",
    icon: "🚀",
    path: "/community/us/product",
    region: "us",
  },
  {
    id: "us-marketing",
    name: "Marketing",
    icon: "📱",
    path: "/community/us/marketing",
    region: "us",
  },
  {
    id: "us-account",
    name: "Sales",
    icon: "💼",
    path: "/community/us/account",
    region: "us",
  },
  {
    id: "us-category",
    name: "Category Mgmt",
    icon: "🏷️",
    path: "/community/us/category",
    region: "us",
  },
  {
    id: "us-finance",
    name: "Finance",
    icon: "💰",
    path: "/community/us/finance",
    region: "us",
  },
  {
    id: "us-hr",
    name: "HR",
    icon: "👥",
    path: "/community/us/hr",
    region: "us",
  },
  {
    id: "us-analyst",
    name: "Analytics",
    icon: "📈",
    path: "/community/us/analyst",
    region: "us",
  },
  {
    id: "us-strategy",
    name: "Strategy",
    icon: "🎯",
    path: "/community/us/strategy",
    region: "us",
  },
];

const ChannelSelector = ({
  currentPath = "/community",
  onChannelSelect,
  userLocation = "india",
}) => {
  const [activeChannel, setActiveChannel] = useState(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const scrollContainerRef = useRef(null);

  // Filter channels based on user location
  const channels = allChannels.filter(
    (ch) => ch.category === "hub" || ch.region === userLocation
  );

  useEffect(() => {
    // Set active channel based on current path
    const active = channels.find((ch) => ch.path === currentPath);
    if (active) setActiveChannel(active.id);
  }, [currentPath, channels]);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setShowLeftArrow(container.scrollLeft > 10);
    setShowRightArrow(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 10
    );
  };

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 200;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const handleChannelClick = (channel) => {
    setActiveChannel(channel.id);
    if (onChannelSelect) {
      onChannelSelect(channel);
    }
  };

  return (
    <div className="relative bg-black border-b border-zinc-800">
      {/* Mobile View - Horizontal Scrollable */}
      <div className="md:hidden relative">
        {/* Left Arrow */}
        {showLeftArrow && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => scroll("left")}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gradient-to-r from-black to-transparent pl-2 pr-8 py-6"
          >
            <div className="bg-zinc-800 hover:bg-zinc-700 rounded-full p-1.5 shadow-lg transition-colors">
              <ChevronLeft className="w-5 h-5 text-gray-300" />
            </div>
          </motion.button>
        )}

        {/* Channel List - Horizontal Scroll */}
        <div
          ref={scrollContainerRef}
          className="flex gap-3 overflow-x-auto scrollbar-hide px-4 py-4"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {channels.map((channel) => (
            <motion.button
              key={channel.id}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleChannelClick(channel)}
              className={`flex flex-col items-center gap-2 px-4 py-3 rounded-xl flex-shrink-0 min-w-[90px] transition-all duration-500 ease-in-out ${
                activeChannel === channel.id
                  ? "bg-zinc-800 shadow-[0_0_20px_rgba(121,231,8,0.3)] border border-[#79e708]/30"
                  : "bg-zinc-900 hover:bg-zinc-800 border border-transparent"
              }`}
            >
              <span className="text-2xl transition-transform duration-300 ease-in-out">
                {channel.icon}
              </span>
              <span
                className={`text-xs font-medium whitespace-nowrap text-center transition-colors duration-500 ease-in-out ${
                  activeChannel === channel.id
                    ? "text-[#79e708]"
                    : "text-gray-300"
                }`}
              >
                {channel.name}
              </span>
            </motion.button>
          ))}
        </div>

        {/* Right Arrow */}
        {showRightArrow && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => scroll("right")}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-gradient-to-l from-black to-transparent pr-2 pl-8 py-6"
          >
            <div className="bg-zinc-800 hover:bg-zinc-700 rounded-full p-1.5 shadow-lg transition-colors">
              <ChevronRight className="w-5 h-5 text-gray-300" />
            </div>
          </motion.button>
        )}
      </div>

      {/* Hide scrollbar CSS */}
      <style>{`
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`}</style>
    </div>
  );
};

export default ChannelSelector;
